import { useState, useMemo } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useQuery } from '@tanstack/react-query'
import { Button, Text, Spinner, ToggleButton, makeStyles, tokens } from '@fluentui/react-components'
import { ChevronLeft24Regular, ChevronRight24Regular, PeopleRegular } from '@fluentui/react-icons'
import { absenceRequestsService, AbsenceRequestStatus, DayHalf, DAY_HALF_LABEL } from '../../services/absenceRequestsService'
import { publicHolidaysService } from '../../services/publicHolidaysService'
import { useAuth } from '../../auth/useAuth'

// Barvy pro typy nepřítomnosti
const TYPE_COLORS: Record<string, string> = {
  VACATION:     '#0078d4',  // modrá       — dovolená
  SICK:         '#d13438',  // červená      — nemoc
  UNPAID:       '#797775',  // šedá         — neplacené volno
  OBSTACLE:     '#ca5010',  // oranžová     — překážka v práci
  COMPENSATORY: '#038387',  // tyrkysová    — náhradní volno
  OCR:          '#8764b8',  // fialová      — ošetřování člena rodiny
  PAID_SPECIAL: '#107c10',  // zelená       — placené volno
  QUARANTINE:   '#4f6bed',  // modrofialová — karanténa
}
const DEFAULT_COLOR = '#8a8886'

const DAYS_CS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']
const MONTHS_CS = ['Leden','Únor','Březen','Duben','Květen','Červen',
                   'Červenec','Srpen','Září','Říjen','Listopad','Prosinec']

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px' },
  header: { display: 'flex', alignItems: 'center', gap: '12px' },
  tableWrap: { overflowX: 'auto' },
  table: {
    borderCollapse: 'collapse',
    minWidth: '100%',
    fontSize: '12px',
  },
  legend: { display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' },
})

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = []
  const d = new Date(year, month, 1)
  while (d.getMonth() === month) {
    days.push(new Date(d))
    d.setDate(d.getDate() + 1)
  }
  return days
}

function isWeekend(date: Date) {
  const day = date.getDay()
  return day === 0 || day === 6
}

// Vrátí kód dne ve formátu YYYY-MM-DD (lokální čas, ne UTC — jinak by CET posunulo datum)
function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseDate(str: string): Date {
  return new Date(str + 'T00:00:00')
}

export default function AbsenceCalendarPage() {
  const styles = useStyles()
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth()) // 0-based
  const [showAll, setShowAll] = useState(false)

  const { isHR, isAdmin, isManager } = useAuth()
  // HR a Admin vždy vidí vše — přepínač se nezobrazí
  const canToggle = !isHR && !isAdmin

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1) }
    else setMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1) }
    else setMonth(m => m + 1)
  }

  const { data: absences, isLoading, isError, refetch } = useQuery({
    queryKey: ['absences-calendar', year, showAll],
    queryFn: () => absenceRequestsService.getAll({
      status: AbsenceRequestStatus.Approved,
      year,
      showAll: canToggle ? showAll : undefined,
    }),
  })

  const { data: holidays } = useQuery({
    queryKey: ['public-holidays', year],
    queryFn: () => publicHolidaysService.getByYear(year),
    staleTime: 10 * 60_000,
  })
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>() // dateKey → name
    holidays?.forEach(h => map.set(h.date, h.name))
    return map
  }, [holidays])

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  const days = useMemo(() => getDaysInMonth(year, month), [year, month])

  // Filtrujeme pouze nepřítomnosti překrývající se s aktuálním měsícem
  const filteredAbsences = useMemo(() => {
    if (!absences) return []
    const monthStart = new Date(year, month, 1)
    const monthEnd = new Date(year, month + 1, 0)
    return absences.filter(a => {
      const start = parseDate(a.startDate)
      const end = parseDate(a.endDate)
      return start <= monthEnd && end >= monthStart
    })
  }, [absences, year, month])

  // Unikátní zaměstnanci
  const employees = useMemo(() => {
    const map = new Map<number, string>()
    filteredAbsences.forEach(a => map.set(a.employeeId, a.employeeFullName))
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'cs'))
  }, [filteredAbsences])

  // Pro každého zaměstnance a každý den najdeme nepřítomnost
  type DayInfo = { code: string; color: string; label: string; dayHalf: DayHalf } | null
  const absenceMap = useMemo(() => {
    const map = new Map<string, DayInfo>() // klíč: `${employeeId}-${dateKey}`
    filteredAbsences.forEach(a => {
      const start = parseDate(a.startDate)
      const end = parseDate(a.endDate)
      const cur = new Date(start)
      while (cur <= end) {
        const key = `${a.employeeId}-${toDateKey(cur)}`
        // Odvoď kód z názvu (nebo použij první 4 znaky)
        const name = a.absenceTypeName ?? ''
        const code = name.startsWith('Dov') ? 'VACATION'
          : name.startsWith('Nem') ? 'SICK'
          : name.startsWith('Nep') ? 'UNPAID'
          : name.startsWith('Pře') ? 'OBSTACLE'
          : name.startsWith('Náh') ? 'COMPENSATORY'
          : name.startsWith('Oše') ? 'OCR'
          : name.startsWith('Pla') ? 'PAID_SPECIAL'
          : name.startsWith('Kar') ? 'QUARANTINE'
          : 'OTHER'
        map.set(key, {
          code,
          color: TYPE_COLORS[code] ?? DEFAULT_COLOR,
          label: a.absenceTypeName + (a.dayHalf !== DayHalf.Full ? ` (${DAY_HALF_LABEL[a.dayHalf]})` : ''),
          dayHalf: a.dayHalf,
        })
        cur.setDate(cur.getDate() + 1)
      }
    })
    return map
  }, [filteredAbsences])

  const todayKey = toDateKey(today)

  return (
    <div className={styles.root}>
      {/* Hlavička s navigací */}
      <div className={styles.header}>
        <Text size={600} weight="semibold">Kalendář nepřítomností</Text>
        <Button appearance="subtle" icon={<ChevronLeft24Regular />} onClick={prevMonth} />
        <Text size={500} weight="semibold" style={{ minWidth: 180, textAlign: 'center' }}>
          {MONTHS_CS[month]} {year}
        </Text>
        <Button appearance="subtle" icon={<ChevronRight24Regular />} onClick={nextMonth} />
        <Button appearance="secondary" size="small" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()) }}>
          Dnes
        </Button>
        {canToggle && (
          <ToggleButton
            size="small"
            icon={<PeopleRegular />}
            checked={showAll}
            onClick={() => setShowAll(s => !s)}
          >
            {showAll
              ? isManager ? 'Zobrazit můj tým' : 'Zobrazit moji nepřítomnost'
              : 'Zobrazit celou firmu'}
          </ToggleButton>
        )}
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst kalendář nepřítomností."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {isLoading && <Spinner label="Načítám..." />}

      {!isLoading && employees.length === 0 && (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          V tomto měsíci nejsou žádné schválené nepřítomnosti.
        </Text>
      )}

      {!isLoading && employees.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {/* Záhlaví: jméno zaměstnance */}
                <th style={{
                  textAlign: 'left', padding: '6px 12px',
                  background: tokens.colorNeutralBackground3,
                  borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
                  minWidth: 160, position: 'sticky', left: 0, zIndex: 2,
                }}>
                  Zaměstnanec
                </th>
                {/* Záhlaví: dny */}
                {days.map(d => {
                  const key = toDateKey(d)
                  const weekend = isWeekend(d)
                  const isToday = key === todayKey
                  const holidayName = holidayMap.get(key)
                  const isHoliday = !!holidayName
                  return (
                    <th key={key} title={holidayName ?? undefined} style={{
                      width: 32, minWidth: 32, padding: '4px 2px',
                      textAlign: 'center',
                      background: isToday
                        ? tokens.colorBrandBackground2
                        : (weekend || isHoliday) ? tokens.colorNeutralBackground4 : tokens.colorNeutralBackground3,
                      borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
                      color: isToday
                        ? tokens.colorNeutralForeground1
                        : isHoliday ? tokens.colorPaletteRedForeground1
                        : weekend  ? tokens.colorNeutralForeground3
                        : tokens.colorNeutralForeground1,
                      fontWeight: isToday ? 700 : 400,
                      cursor: isHoliday ? 'help' : 'default',
                    }}>
                      <div>{d.getDate()}</div>
                      <div style={{ fontSize: 10, fontWeight: 400 }}>{DAYS_CS[(d.getDay() + 6) % 7]}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, rowIdx) => (
                <tr key={emp.id} style={{
                  background: rowIdx % 2 === 0 ? tokens.colorNeutralBackground1 : tokens.colorNeutralBackground2,
                }}>
                  {/* Jméno — fixní sloupec */}
                  <td style={{
                    padding: '4px 12px',
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    fontWeight: 500,
                    position: 'sticky', left: 0, zIndex: 1,
                    background: rowIdx % 2 === 0 ? tokens.colorNeutralBackground1 : tokens.colorNeutralBackground2,
                    whiteSpace: 'nowrap',
                  }}>
                    {emp.name}
                  </td>
                  {/* Buňky dnů */}
                  {days.map(d => {
                    const dayKey = toDateKey(d)
                    const info = absenceMap.get(`${emp.id}-${dayKey}`)
                    const weekend = isWeekend(d)
                    const isToday = dayKey === todayKey
                    const isHoliday = holidayMap.has(dayKey)
                    return (
                      <td key={dayKey} title={info?.label ?? (isHoliday ? holidayMap.get(dayKey) : '')} style={{
                        padding: '2px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                        background: isToday
                          ? tokens.colorBrandBackground2
                          : (weekend || isHoliday) ? tokens.colorNeutralBackground4 : 'transparent',
                      }}>
                        {info && (
                          <div style={{
                            background: info.dayHalf === DayHalf.Morning
                              ? `linear-gradient(to right, ${info.color} 0 50%, transparent 50% 100%)`
                              : info.dayHalf === DayHalf.Afternoon
                                ? `linear-gradient(to right, transparent 0 50%, ${info.color} 50% 100%)`
                                : info.color,
                            borderRadius: 3,
                            height: 20,
                            width: '100%',
                          }} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legenda */}
      <div className={styles.legend}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 16, height: 16, borderRadius: 3, background: tokens.colorNeutralBackground4, border: `1px solid ${tokens.colorNeutralStroke1}` }} />
          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>Státní svátek</Text>
        </div>
        {Object.entries({
          VACATION:     'Dovolená',
          SICK:         'Nemoc',
          UNPAID:       'Neplacené volno',
          OBSTACLE:     'Překážka v práci',
          COMPENSATORY: 'Náhradní volno',
          OCR:          'Ošetřování člena rodiny',
          PAID_SPECIAL: 'Placené volno',
          QUARANTINE:   'Karanténa',
        }).map(([code, label]) => (
          <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 3, background: TYPE_COLORS[code] }} />
            <Text size={200}>{label}</Text>
          </div>
        ))}
      </div>
    </div>
  )
}
